import { useEffect, useState } from 'react';
import { BootResponse } from '../constants/apiResponses.types';
import { isAndroid, isIOS } from 'react-device-detect';
import { EEventsType, ESessionStorageKeys, EStorePhase } from 'constants/enums';
import { webStorageUtil } from 'state/webStorage.state.service';
import useApi from './useApi';
import useCustomEvents from './useCustomEvents';
import { getPlatformData } from 'utils';
import { useAppState } from 'state/hooks/app.state.hook';

export const useSaveToHomePage = () => {
  const API = useApi({});
  const publisherMetaData = API.getPublisherMeta.data as BootResponse;
  const customEvents = useCustomEvents();
  const { installPrompt, setInstallPrompt } = useAppState();

  const [isSaveToHomePageOpen, setIsSaveToHomePageOpen] = useState(false);
  const [isDisplayAddToHomePageButton, setIsDisplayAddToHomePageButton] = useState(
    !!publisherMetaData.storeTheme.storeScreen.addToHomeScreen?.active &&
      (isIOS || isAndroid) &&
      !(webStorageUtil.get(ESessionStorageKeys.SOURCE) === 'home screen bookmark')
  );
  const [isAndroidFallBack, setIsAndroidFallBack] = useState(false);

  useEffect(() => {
    const handleAppInstalled = () => {
      setIsDisplayAddToHomePageButton(false);
      webStorageUtil.set(ESessionStorageKeys.SOURCE, 'home screen bookmark');

      customEvents.sendCustomEvent(
        EEventsType.HOME_SCREEN_INSTALLED_ANDROID,
        {
          platform: getPlatformData(),
          add_to_home_screen_appear: isDisplayAddToHomePageButton,
          source: webStorageUtil.get(ESessionStorageKeys.SOURCE),
        },
        EStorePhase.POST_LOGIN
      );
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallApp = async () => {
    if (installPrompt) {
      try {
        await installPrompt.prompt();
        setInstallPrompt(null);
        webStorageUtil.remove(ESessionStorageKeys.INSTALL_PROMPT_FIRED);
      } catch (error) {
        console.error('Install prompt failed', error);
      }
    } else {
      setIsAndroidFallBack(true);
      console.log('Install prompt not available');

      customEvents.sendCustomEvent(
        EEventsType.HOME_SCREEN_GUIDE_PRESENTED,
        {
          platform: getPlatformData(),
          add_to_home_screen_appear: isDisplayAddToHomePageButton,
          source: webStorageUtil.get(ESessionStorageKeys.SOURCE),
        },
        EStorePhase.POST_LOGIN
      );
    }
  };

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

    if (isStandalone) {
      setIsDisplayAddToHomePageButton(false);
      setIsSaveToHomePageOpen(false);
      webStorageUtil.set(ESessionStorageKeys.SOURCE, 'home screen bookmark');
    } else if (webStorageUtil.get(ESessionStorageKeys.INSTALL_PROMPT_FIRED)) {
      // Handle case where beforeinstallprompt already fired but page reloaded
      console.log('Restoring install prompt state');
      setIsDisplayAddToHomePageButton(true);
    }
  }, []);

  const handleCloseSaveToHomePage = () => {
    setIsSaveToHomePageOpen(false);
    setIsAndroidFallBack(false);
  };

  return {
    isSaveToHomePageOpen,
    setIsSaveToHomePageOpen,
    isDisplayAddToHomePageButton,
    handleCloseSaveToHomePage,
    handleInstallApp,
    isAndroidFallBack,
  };
};
